import React, { useState, useEffect, useRef, useMemo } from "react";
import { MDBContainer } from "mdb-react-ui-kit";
import { useNavigate } from "react-router-dom";
import { ActionButton, DataTable } from "../../ui";
import { api } from "../../../services";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ConfirmModal from "../../Modals/ConfirmModal";
import Toolbar from "../../Toolbar";

// Font Awesome icons are used for Edit/Delete buttons as per UI
function AdminAppManagementTable() {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const [availableApps, setAvailableApps] = useState([]);
  const [filteredApps, setFilteredApps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [tableSortColumn, setTableSortColumn] = useState("platform");
  const [sortDirection, setSortDirection] = useState("asc");
  const [openTableStatusDropdown, setOpenTableStatusDropdown] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersFetched, setUsersFetched] = useState(false);
  const [showQAModal, setShowQAModal] = useState(false);
  const [selectedAppForQA, setSelectedAppForQA] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [modalData, setModalData] = useState({
    title: "",
    message: "",
    action: () => {},
    actionData: null,
    visibility: null,
  });
  const navigate = useNavigate();
  const rowButtonRefs = useRef({});
  const [fixedDropdown, setFixedDropdown] = useState({ appId: null, top: 0, left: 0, width: 176 });
  const scrollOriginRef = useRef(null);
  const fixedMenuRef = useRef(null);
  const buttonRectRef = useRef(null);

  // Track fixed dropdown position on scroll/resize so it moves with the button
  useEffect(() => {
    if (!fixedDropdown.appId) return;

    let rafId = null;

    const closeDropdown = () => {
      setFixedDropdown({ appId: null, top: 0, left: 0, width: 176 });
      setOpenTableStatusDropdown(null);
      scrollOriginRef.current = null;
    };

    const updatePosition = () => {
      const btn = rowButtonRefs.current[fixedDropdown.appId];
      if (!btn || !btn.getBoundingClientRect) {
        closeDropdown();
        return;
      }
      const rect = btn.getBoundingClientRect();
      buttonRectRef.current = rect;
      const menuWidth = fixedDropdown.width || 176;
      let left = rect.left;
      if (left + menuWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - menuWidth - 8);
      }

      const openUp = !!fixedDropdown.openUp;
      const menuEl = fixedMenuRef.current;
      const measuredHeight = openUp ? (menuEl ? (menuEl.offsetHeight || menuEl.getBoundingClientRect().height) : (fixedDropdown.menuHeight || 200)) : 0;
      const top = openUp ? Math.max(8, rect.top - measuredHeight - 4) : rect.bottom + 4;

      setFixedDropdown(prev => (prev.appId === fixedDropdown.appId ? { ...prev, top, left } : prev));
    };

    const onScrollOrResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updatePosition);
    };

    // initial sync
    updatePosition();

    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('touchmove', onScrollOrResize, { passive: true });

    // attach to any scrollable ancestors of the button so nested scrolls also update
    const btn = rowButtonRefs.current[fixedDropdown.appId];
    const scrollableListeners = [];
    if (btn) {
      let el = btn.parentElement;
      while (el && el !== document.body) {
        try {
          const cs = getComputedStyle(el);
          const overflowY = cs.overflowY;
          if ((overflowY && overflowY !== 'visible') && el.scrollHeight > el.clientHeight) {
            el.addEventListener('scroll', onScrollOrResize, { passive: true });
            scrollableListeners.push(el);
          }
        } catch (e) {}
        el = el.parentElement;
      }
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('touchmove', onScrollOrResize);
      scrollableListeners.forEach(el => el.removeEventListener('scroll', onScrollOrResize));
    };
  }, [fixedDropdown.appId, fixedDropdown.openUp, fixedDropdown.width, fixedDropdown.menuHeight]);

  // When opening upwards, measure the actual rendered menu height and adjust top
  useEffect(() => {
    if (!fixedDropdown.appId) return;
    if (!fixedDropdown.openUp) return;
    const el = fixedMenuRef.current;
    const rect = buttonRectRef.current;
    if (!el || !rect) return;

    // measure actual height and adjust top so menu bottom sits 4px above button
    const measured = el.offsetHeight || el.getBoundingClientRect().height || fixedDropdown.menuHeight || 0;
    const desiredTop = Math.max(8, rect.top - measured - 4);
    if (Math.abs(desiredTop - fixedDropdown.top) > 1) {
      setFixedDropdown(prev => ({ ...prev, top: desiredTop }));
    }
  }, [fixedDropdown.appId, fixedDropdown.openUp]);

  // Close table status dropdown when clicking outside (including fixed-position menu)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        openTableStatusDropdown &&
        !event.target.closest("[data-status-dropdown]") &&
        !event.target.closest("[data-status-dropdown-fixed]")
      ) {
        setOpenTableStatusDropdown(null);
        setFixedDropdown({ appId: null, top: 0, left: 0, width: 176 });
      }
    };

    if (openTableStatusDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openTableStatusDropdown]);

  const THEME_COLOR = "#083B4C"; // Dark teal from image

  const styles = {
    container: {
      backgroundColor: "#F0F0F0",
      minHeight: "100vh",
      padding: 0,
      fontFamily: "'Inter', sans-serif",
      direction: isArabic ? 'rtl' : 'ltr',
    },
    pageTitle: {
      fontSize: "40px",
      fontWeight: "700",
      color: "#1a1a1a",
      marginBottom: "12px",
      textAlign: isArabic ? 'right' : 'left',
    },
    pageSubtitle: {
      fontSize: "14px",
      color: "#6D6D6D",
      marginBottom: '28px',
      textAlign: isArabic ? 'right' : 'left',
    },
    filterCard: {
      background: "white",
      paddingTop: 14,
      paddingBottom: 14,
      paddingLeft: 16,
      paddingRight: 16,
      borderRadius: "10px",
      boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    },
    label: {
      display: "block",
      fontWeight: "500",
      marginBottom: "6px",
      fontSize: "14px",
      color: "#030F1B",
    },
    selectField: {
      width: "100%",
      padding: "8px 12px",
      borderRadius: "8px",
      border: "1px solid #dcdcdc",
      fontSize: "14px",
      height: "36px",
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.06)",
      transition: "box-shadow 0.2s ease-in-out",
    },
    addNewBtn: {
      backgroundColor: THEME_COLOR,
      color: "white",
      border: "none",
      padding: "6px 12px",
      borderRadius: "4px",
      fontWeight: "500",
      height: "29px",
      fontSize: "14px",
      display: "flex",
      alignItems: "center",
      gap: "2px",
      cursor: "pointer",
    },
    tableWrapper: {
      background: "white",
      borderRadius: "10px",
      overflow: "hidden",
      boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    },
    integrationHeader: {
      paddingLeft: isArabic ? '16px' : '12px',
      paddingRight: isArabic ? '12px' : '16px',
      paddingTop: '9px',
      paddingBottom: '9px',
      color: '#030F1B',
      fontSize: "18px",
      fontWeight: "600",
      borderBottom: "1px solid #eee",
      textAlign: isArabic ? 'right' : 'left',
    },
    tableHeader: {
      backgroundColor: THEME_COLOR,
    },
    th: {
      color: "white",
      fontWeight: "600",
      padding: "1rem",
      fontSize: "16px",
      border: "none",
      verticalAlign: "middle",
    },
    td: {
      padding: "1rem",
      verticalAlign: "middle",
      fontSize: "0.9rem",
      borderBottom: "1px solid #f0f0f0",
    },
    appLogo: {
      width: "80px",
      height: "40px",
      objectFit: "contain",
    },
    platformText: {
      fontWeight: "700",
      color: "#333",
    },
    descriptionText: {
      color: "#888",
      maxWidth: "300px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    actionBtn: {
      padding: "0.2rem 0.7rem",
      fontSize: "0.85rem",
      borderRadius: "4px",
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      margin: "0 4px",
      background: "white",
      cursor: "pointer",
      transition: "0.2s",
    },
    editBtn: { border: "1px solid #118AB2", color: "#118AB2" },
    deleteBtn: { border: "1px solid #E84A4A", color: "#E84A4A" },
    paginationContainer: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "1.5rem",
    },
    pageNumber: {
      width: "32px",
      height: "32px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "1px solid #dee2e6",
      borderRadius: "4px",
      margin: "0 2px",
      cursor: "pointer",
      fontSize: "0.85rem",
    },
    activePage: {
      backgroundColor: THEME_COLOR,
      color: "white",
      borderColor: THEME_COLOR,
    }
  };

  useEffect(() => {
    fetchAvailableApps();
    fetchUsers();
  }, []);

  useEffect(() => {
    filterApps();
  }, [statusFilter, availableApps]);

  const fetchAvailableApps = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const response = await api.get("/admin/available-apps", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailableApps(response.data.apps || []);
    } catch (error) {
      console.error("Error fetching available apps:", error.message);
      const errorMessage = error.response?.data?.message || t("Failed to fetch apps. Please try again later.");
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    if (usersFetched) return;
    
    try {
      setLoadingUsers(true);
      const token = localStorage.getItem("adminToken");
      const response = await api.get("/admin/users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data.users || []);
      setUsersFetched(true);
    } catch (error) {
      console.error("Error fetching users:", error);
      const errorMessage = error.response?.data?.message || t("Failed to fetch users");
      toast.error(errorMessage);
    } finally {
      setLoadingUsers(false);
    }
  };

  const filterApps = () => {
    let filtered = statusFilter === "All" ? availableApps : availableApps.filter(app => app.status === statusFilter);
    setFilteredApps(filtered);
    setCurrentPage(1);
  };

  const handleAddNewApp = () => {
    navigate("/admin/add-app");
  };

  const handleEditApp = (app) => {
    navigate("/admin/edit-app", { state: { app, users } });
  };

  const handleDeleteApp = (appId) => {
    setModalData({
      title: t("Confirm Delete"),
      message: t("Are you sure you want to delete this app?"),
      action: confirmDeleteApp,
      actionData: appId,
      visibility: null,
    });
    setShowConfirm(true);
  };

  const confirmDeleteApp = async (appId) => {
    try {
      const token = localStorage.getItem("adminToken");
      await api.delete(`/admin/available-apps/${appId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchAvailableApps();
      toast.success(`${t("App deleted successfully!")}`);
    } catch (error) {
      console.error("Error deleting app:", error.message);
      const errorMessage = error.response?.data?.message || t("Failed to delete app. Please try again.");
      toast.error(errorMessage);
    }
  };

  const handleStatusChange = (appId, newStatus) => {
    const app = availableApps.find((a) => a._id === appId);
    
    // If changing to QA Testing, show user selection modal
    if (newStatus === "QA Testing") {
      setSelectedAppForQA({ ...app, newStatus });
      setSelectedUsers(app.allowedUsers || []);
      setShowQAModal(true);
      setOpenTableStatusDropdown(null);
    } else {
      // For other statuses, update immediately
      updateAppStatus(appId, newStatus, []);
    }
  };

  const updateAppStatus = async (appId, newStatus, allowedUsers = []) => {
    try {
      const token = localStorage.getItem("adminToken");
      const app = availableApps.find((a) => a._id === appId);
      
      // Build payload similar to AdminEditApp.js
      const payload = {
        id: app._id,
        name: app.name,
        description: app.description,
        name_ar: app.name_ar,
        description_ar: app.description_ar,
        logo: app.logo,
        apiUrl: app.apiUrl,
        integrationMethod: app.integrationMethod,
        integrationType: app.integrationType,
        status: newStatus,
        credentialsTemplate: app.credentialsTemplate,
        appType: app.appType,
        providerKey: app.providerKey,
        webhookToken: app.webhookToken,
        appId: app.appId,
        authorizationUrl: app.authorizationUrl,
        category: app.category,
        // Include allowedUsers only if status is QA Testing
        ...(newStatus === "QA Testing" && allowedUsers.length > 0 && { allowedUsers }),
      };

      // Remove undefined/empty values
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(([_, v]) => v !== undefined && v !== null && v !== "")
      );

      // Use POST method like AdminEditApp
      await api.post(`/admin/available-apps`, filteredPayload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Update local state
      setAvailableApps((prev) =>
        prev.map((a) => (a._id === appId ? { ...a, status: newStatus, allowedUsers } : a))
      );
      toast.success(`${t("App status updated successfully!")}`);
    } catch (error) {
      console.error("Error updating app status:", error.message);
      const errorMessage = error.response?.data?.message || t("Failed to update status. Please try again.");
      toast.error(errorMessage);
    }
  };

  const handleQATestingSubmit = () => {
    // Validate at least one user is selected
    if (selectedUsers.length === 0) {
      toast.error(`${t("Please select at least one user for QA Testing")}`);
      return;
    }

    updateAppStatus(selectedAppForQA._id, "QA Testing", selectedUsers);
    setShowQAModal(false);
    setSelectedAppForQA(null);
    setSelectedUsers([]);
    setSearchTerm("");
  };

  const handleUserToggle = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    const filtered = filteredUsersForModal;
    if (selectedUsers.length === filtered.length && filtered.length > 0) {
      setSelectedUsers([]);
    } else {
      const allUserIds = filtered.map(user => user._id || user.id);
      setSelectedUsers(allUserIds);
    }
  };

  const getUserDisplay = (user) => {
    const displayName = user.name || user.username || user.email;
    const emailSuffix = user.email && user.name ? ` (${user.email})` : '';
    return `${displayName}${emailSuffix}`;
  };

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  // Filtered users for modal search
  const filteredUsersForModal = users.filter(user => {
    const userName = user.name || user.username || user.email || '';
    return userName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Pagination calculations
  const sortedApps = useMemo(() => {
    const list = [...filteredApps];
    const dir = sortDirection === "asc" ? 1 : -1;
    list.sort((a, b) => {
      let av;
      let bv;
      switch (tableSortColumn) {
        case "description": {
          const aDesc = isArabic && a.description_ar ? a.description_ar : a.description;
          const bDesc = isArabic && b.description_ar ? b.description_ar : b.description;
          av = (aDesc || "").toLowerCase();
          bv = (bDesc || "").toLowerCase();
          break;
        }
        case "status":
          av = (a.status || "").toLowerCase();
          bv = (b.status || "").toLowerCase();
          break;
        default: {
          const aName = isArabic && a.name_ar ? a.name_ar : a.name;
          const bName = isArabic && b.name_ar ? b.name_ar : b.name;
          av = (aName || "").toLowerCase();
          bv = (bName || "").toLowerCase();
        }
      }
      return dir * av.localeCompare(bv);
    });
    return list;
  }, [filteredApps, tableSortColumn, sortDirection, isArabic]);

  const totalItems = sortedApps.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = sortedApps.slice(startIndex, endIndex);

  const renderStatusCell = (app, index) => (
    <div style={{ position: "relative", display: "inline-block" }} data-status-dropdown>
      <button
        type="button"
        style={{
          padding: "6px 12px",
          fontSize: "13px",
          border: "1px solid #e5e7eb",
          borderRadius: "6px",
          backgroundColor: "#ffffff",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          width: "176px",
          justifyContent: "space-between",
        }}
        ref={(el) => (rowButtonRefs.current[app._id] = el)}
        onClick={() => {
          const currentlyOpen = fixedDropdown.appId === app._id;
          if (currentlyOpen) {
            setFixedDropdown({ appId: null, top: 0, left: 0, width: 176 });
            setOpenTableStatusDropdown(null);
            return;
          }

          const btn = rowButtonRefs.current[app._id];
          if (btn && btn.getBoundingClientRect) {
            const rect = btn.getBoundingClientRect();
            buttonRectRef.current = rect;
            const menuWidth = 176;
            let left = rect.left;
            if (left + menuWidth > window.innerWidth) {
              left = Math.max(8, window.innerWidth - menuWidth - 8);
            }

            const statusesCount = ["Active", "Pending", "Coming Soon", "Inactive", "QA Testing"].length;
            const estimatedOptionHeight = 40;
            const menuHeight = Math.min(200, statusesCount * estimatedOptionHeight);
            const openUp = index >= Math.max(0, currentItems.length - 3);
            const top = openUp ? Math.max(8, rect.top - menuHeight - 4) : rect.bottom + 4;

            setFixedDropdown({ appId: app._id, top, left, width: menuWidth, openUp, menuHeight });
            setOpenTableStatusDropdown(app._id);

            try {
              const windowY = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
              const ancestors = [];
              let el = btn.parentElement;
              while (el && el !== document.body) {
                const cs = getComputedStyle(el);
                const overflowY = cs.overflowY;
                if (overflowY && overflowY !== "visible" && el.scrollHeight > el.clientHeight) {
                  ancestors.push({ el, scrollTop: el.scrollTop });
                }
                el = el.parentElement;
              }
              scrollOriginRef.current = { windowY, ancestors };
            } catch (e) {
              scrollOriginRef.current = null;
            }
          } else {
            setFixedDropdown({ appId: app._id, top: 0, left: 0, width: 176 });
            setOpenTableStatusDropdown(app._id);
          }
        }}
      >
        <span>{t(app.status)}</span>
        <i
          className="bi bi-chevron-down"
          style={{
            fontSize: "12px",
            transition: "transform 0.3s ease",
            transform: fixedDropdown.appId === app._id ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </button>
    </div>
  );

  const appColumns = useMemo(
    () => [
      {
        id: "logo",
        header: t("Logo"),
        render: (app) => (
          <>
            {app.logo ? (
              <img
                src={app.logo}
                alt="logo"
                style={styles.appLogo}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
            ) : null}
            <div
              style={{
                width: "80px",
                height: "40px",
                borderRadius: "8px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                display: app.logo ? "none" : "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.2rem",
                fontWeight: 700,
              }}
            >
              {(isArabic && app.name_ar ? app.name_ar : app.name).charAt(0).toUpperCase()}
            </div>
          </>
        ),
      },
      {
        id: "platform",
        header: t("Platform"),
        sortable: true,
        strong: true,
        render: (app) => (isArabic && app.name_ar ? app.name_ar : app.name),
      },
      {
        id: "description",
        header: t("Description"),
        sortable: true,
        render: (app) =>
          truncateText(
            isArabic && app.description_ar ? app.description_ar : app.description,
            80
          ),
      },
      {
        id: "status",
        header: t("Status"),
        sortable: true,
        width: "196px",
        render: (app, index) => renderStatusCell(app, index),
      },
      {
        id: "actions",
        header: t("Action"),
        hideable: false,
        render: (app) => (
          <div className="oh-table-actions">
            <ActionButton variant="edit" onClick={() => handleEditApp(app)}>
              {t("Edit")}
            </ActionButton>
            <ActionButton variant="delete" onClick={() => handleDeleteApp(app._id)}>
              {t("Delete")}
            </ActionButton>
          </div>
        ),
      },
    ],
    [t, isArabic, fixedDropdown.appId, currentItems.length]
  );

  const emptyMessage =
    statusFilter === "All"
      ? t("No apps available. Click 'Add New App' to get started.")
      : t(`No apps with status: ${statusFilter}`);

  const statusFilterOptions = useMemo(
    () =>
      ["All", "Active", "Pending", "Coming Soon", "Inactive", "QA Testing"].map(
        (status) => ({
          id: status,
          name: t(status),
        })
      ),
    [t]
  );

  return (
    <div className="admin-page-root oh-page" dir={isArabic ? "rtl" : "ltr"}>
    <MDBContainer fluid className="content-area flex-grow-1">
      <h1 className="page-title-header">{t("Manage Apps")}</h1>

      <div className="oh-page-section">
        <Toolbar
          showFilter
          orderStatuses={statusFilterOptions}
          activeTab={statusFilter}
          setActiveTab={setStatusFilter}
          showViewMode={false}
        />
      </div>

      <div className="oh-page-section">
      <DataTable
        tableId="admin-app-management"
        title={t("Integration Apps")}
        selectionMode="index"
        columns={appColumns}
        data={currentItems}
        rowKey="_id"
        loading={isLoading}
        isRtl={isArabic}
        primaryAction={{
          label: t("Add New App"),
          onClick: handleAddNewApp,
        }}
        emptyState={emptyMessage}
        sort={{
          column: tableSortColumn,
          direction: sortDirection,
          onChange: ({ column, direction }) => {
            setTableSortColumn(column);
            setSortDirection(direction);
            setCurrentPage(1);
          },
        }}
        pagination={{
          page: currentPage,
          pageSize: itemsPerPage,
          total: totalItems,
          onPageChange: setCurrentPage,
          onPageSizeChange: (n) => {
            setItemsPerPage(n);
            setCurrentPage(1);
          },
          pageSizeOptions: [10, 25, 50],
          showLabel: t("Show"),
          ofLabel: t("of"),
          resultsLabel: t("results"),
          isRtl: isArabic,
          previousLabel: t("previous", { defaultValue: "Previous" }),
          nextLabel: t("next", { defaultValue: "Next" }),
        }}
      />
      </div>

      {fixedDropdown.appId && (() => {
              const app = availableApps.find(a => a._id === fixedDropdown.appId) || currentItems.find(a => a._id === fixedDropdown.appId);
              if (!app) return null;
              return (
                <div
                  data-status-dropdown-fixed
                  ref={fixedMenuRef}
                  style={{
                    position: 'fixed',
                    top: `${fixedDropdown.top}px`,
                    left: `${fixedDropdown.left}px`,
                    width: `${fixedDropdown.width}px`,
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    zIndex: 2000,
                    maxHeight: '200px',
                    overflowY: 'auto',
                  }}
                >
                  {["Active", "Pending", "Coming Soon", "Inactive", "QA Testing"].map((status) => (
                    <div
                      key={status}
                      className={`custom-dropdown-item${app.status === status ? ' active' : ''}`}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: '13px',
                      }}
                      onClick={() => {
                        handleStatusChange(app._id, status);
                        // Close fixed dropdown unless QA modal will open
                        if (status !== 'QA Testing') {
                          setFixedDropdown({ appId: null, top: 0, left: 0, width: 176 });
                          setOpenTableStatusDropdown(null);
                        } else {
                          // close fixed menu and keep QA modal flow
                          setFixedDropdown({ appId: null, top: 0, left: 0, width: 176 });
                          setOpenTableStatusDropdown(null);
                        }
                      }}
                    >
                      {t(status)}
                    </div>
                  ))}
                </div>
              );
            })()}

      {/* QA Testing User Selection Modal */}
      {showQAModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={() => {
            setShowQAModal(false);
            setSelectedAppForQA(null);
            setSelectedUsers([]);
            setSearchTerm("");
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '2rem',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              direction: isArabic ? 'rtl' : 'ltr',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>
                {t("Select Users for QA Testing")}
              </h3>
              <button
                onClick={() => {
                  setShowQAModal(false);
                  setSelectedAppForQA(null);
                  setSelectedUsers([]);
                  setSearchTerm("");
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#6c757d',
                  padding: '0',
                  lineHeight: '1',
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: isArabic ? 'right' : 'left' }}>
              <strong>{t("App")}:</strong> {isArabic && selectedAppForQA?.name_ar ? selectedAppForQA.name_ar : selectedAppForQA?.name}
              {selectedUsers.length > 0 && (
                <div style={{ marginTop: '0.5rem' }}>
                  <span style={{ 
                    backgroundColor: '#0e3b4d', 
                    color: 'white', 
                    padding: '0.25rem 0.75rem', 
                    borderRadius: '12px',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}>
                    {selectedUsers.length} {t("selected")}
                  </span>
                </div>
              )}
            </div>

            {loadingUsers ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                {t("Loading users...")}
              </div>
            ) : users.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                {t("No users available")}
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <input
                    type="text"
                    placeholder={t("Search users...")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      fontSize: '0.95rem',
                    }}
                  />
                </div>

                {filteredUsersForModal.length > 0 && (
                  <div
                    onClick={handleSelectAll}
                    style={{
                      padding: '0.75rem 1rem',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      marginBottom: '0.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      border: '1px solid #e9ecef',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === filteredUsersForModal.length && filteredUsersForModal.length > 0}
                      readOnly
                      style={{ [isArabic ? 'marginLeft' : 'marginRight']: '0.75rem', cursor: 'pointer', width: '18px', height: '18px' }}
                    />
                    <strong>{t("Select All")}</strong>
                  </div>
                )}

                <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '1.5rem' }}>
                  {filteredUsersForModal.map((user) => {
                    const userId = user._id || user.id;
                    const isSelected = selectedUsers.includes(userId);
                    
                    return (
                      <div
                        key={userId}
                        onClick={() => handleUserToggle(userId)}
                        style={{
                          padding: '0.75rem 1rem',
                          cursor: 'pointer',
                          borderRadius: '6px',
                          marginBottom: '0.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: isSelected ? '#e7f3ff' : 'transparent',
                          border: '1px solid ' + (isSelected ? '#0e3b4d' : 'transparent'),
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) e.currentTarget.style.backgroundColor = '#f8f9fa';
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                          style={{ [isArabic ? 'marginLeft' : 'marginRight']: '0.75rem', cursor: 'pointer', width: '18px', height: '18px' }}
                        />
                        <span>{getUserDisplay(user)}</span>
                      </div>
                    );
                  })}

                  {filteredUsersForModal.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                      {t("No users found")}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setShowQAModal(false);
                      setSelectedAppForQA(null);
                      setSelectedUsers([]);
                      setSearchTerm("");
                    }}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: 'white',
                      color: '#6c757d',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: '500',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    onClick={handleQATestingSubmit}
                    style={{
                      padding: '0.75rem 1.5rem',
                      backgroundColor: '#0e3b4d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: '500',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#0a2d3a';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#0e3b4d';
                    }}
                  >
                    {t("Save")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        title={modalData.title}
        message={modalData.message}
        action={modalData.action}
        actionData={modalData.actionData}
        visibility={modalData.visibility}
      />
    </MDBContainer>
    </div>
  );
}

export default AdminAppManagementTable;